import type { CarrierPrice } from "~/shared/schemas/carrier-schema";
import type {
	Markups as LtlMarkup,
	MarkupsPartials as PartialsMarkup,
	TruckPayMarkup,
} from "~/shared/schemas/markup-schema.ts";

export type MarginCalculationData = {
	ltlMarkups: LtlMarkup | null;
	customerLtlMarkups: LtlMarkup | null;
	partialsMarkups: PartialsMarkup | null;
	customerPartialsMarkups: PartialsMarkup | null;
};

/**
 * Finds the applicable markup bracket for a given carrier rate.
 * Matches Angular logic: sorts descending by start, finds first where start <= carrierRate.
 */
const findApplicableBracket = (
	carrierRate: number,
	brackets: TruckPayMarkup[],
): TruckPayMarkup | null => {
	if (!brackets || brackets.length === 0) {
		return null;
	}

	// Sort descending by start value (highest first)
	const sortedBrackets = [...brackets].sort((a, b) => b.start - a.start);

	// Find first bracket where start <= carrierRate
	return sortedBrackets.find((bracket) => bracket.start <= carrierRate) ?? null;
};

/**
 * Calculates the base margin based on the bracket type (FLAT or PERCENTAGE).
 * Matches Angular logic: FLAT returns markup directly, PERCENTAGE multiplies by 0.01.
 */
const calculateBaseMargin = (
	carrierRate: number,
	bracket: TruckPayMarkup,
): number => {
	if (bracket.type === "FLAT") {
		return bracket.markup;
	}

	// PERCENTAGE - matches Angular: carrierRate * markup * 0.01
	return carrierRate * bracket.markup * 0.01;
};

/**
 * Applies minimum and maximum caps to the calculated margin.
 * Matches Angular logic: check maximum first (if > 0), then minimum.
 */
const applyMarginCaps = (margin: number, bracket: TruckPayMarkup): number => {
	let cappedMargin = margin;

	// Angular checks: if maximum && maximum > 0 && margin > maximum
	if (
		bracket.maximum !== null &&
		bracket.maximum > 0 &&
		cappedMargin > bracket.maximum
	) {
		cappedMargin = bracket.maximum;
	}

	// Angular checks: if minimum && margin < minimum (no > 0 check for minimum)
	if (bracket.minimum !== null && cappedMargin < bracket.minimum) {
		cappedMargin = bracket.minimum;
	}

	return cappedMargin;
};

/**
 * Calculates the broker margin for an LTL carrier based on markup profiles.
 * Uses customer-specific markups if available, otherwise falls back to defaults.
 */
export const calculateLTLMargin = (
	carrierRate: number,
	defaultMarkups: LtlMarkup | null,
	customerMarkups: LtlMarkup | null,
): number => {
	// Validate input
	if (!carrierRate || carrierRate <= 0) {
		return 0;
	}

	// Determine which markup profile to use (customer-specific overrides default)
	// Customer markup takes precedence if it has standardMarkups, otherwise use default
	let brackets: TruckPayMarkup[];

	if (
		customerMarkups?.standardMarkups &&
		customerMarkups.standardMarkups.length > 0
	) {
		brackets = customerMarkups.standardMarkups;
	} else if (
		defaultMarkups?.standardMarkups &&
		defaultMarkups.standardMarkups.length > 0
	) {
		brackets = defaultMarkups.standardMarkups;
	} else {
		return 0;
	}

	// Find applicable bracket
	const bracket = findApplicableBracket(carrierRate, brackets);

	if (!bracket) {
		// No matching bracket found
		return 0;
	}

	// Calculate base margin
	const baseMargin = calculateBaseMargin(carrierRate, bracket);

	// Apply caps
	const finalMargin = applyMarginCaps(baseMargin, bracket);

	return finalMargin;
};

/**
 * Calculates the broker margin for a Partials carrier based on markup profiles.
 * Uses customer-specific markups if available, otherwise falls back to defaults.
 */
export const calculatePartialsMargin = (
	carrierRate: number,
	defaultPartialsMarkups: PartialsMarkup | null,
	customerPartialsMarkups: PartialsMarkup | null,
): number => {
	// Validate input
	if (!carrierRate || carrierRate <= 0) {
		return 0;
	}

	// Determine which markup profile to use (customer-specific overrides default)
	// Customer markup takes precedence if it has truckPayMarkups, otherwise use default
	let brackets: TruckPayMarkup[];

	if (
		customerPartialsMarkups?.truckPayMarkups &&
		customerPartialsMarkups.truckPayMarkups.length > 0
	) {
		brackets = customerPartialsMarkups.truckPayMarkups;
	} else if (
		defaultPartialsMarkups?.truckPayMarkups &&
		defaultPartialsMarkups.truckPayMarkups.length > 0
	) {
		brackets = defaultPartialsMarkups.truckPayMarkups;
	} else {
		// No markup data available - matches Angular error case
		console.error("No Partials markup defaults were found");
		return 0;
	}

	// Find applicable bracket
	const bracket = findApplicableBracket(carrierRate, brackets);

	if (!bracket) {
		// No matching bracket found
		return 0;
	}

	// Calculate base margin
	const baseMargin = calculateBaseMargin(carrierRate, bracket);

	// Apply caps
	const finalMargin = applyMarginCaps(baseMargin, bracket);

	return finalMargin;
};

/**
 * Main margin calculation function that determines carrier type and calculates accordingly.
 */
export const calculateMargin = (
	carrierPrice: CarrierPrice,
	defaultMarkups: LtlMarkup | null,
	customerMarkups: LtlMarkup | null,
	defaultPartialsMarkups: PartialsMarkup | null,
	customerPartialsMarkups: PartialsMarkup | null,
): number => {
	// Determine if this is a Partials carrier
	if (carrierPrice.carrier.isPartials) {
		return calculatePartialsMargin(
			carrierPrice.carrierRate,
			defaultPartialsMarkups,
			customerPartialsMarkups,
		);
	}

	return calculateLTLMargin(
		carrierPrice.carrierRate,
		defaultMarkups,
		customerMarkups,
	);
};
